import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  memo,
} from 'react';
import {
  View,
  useWindowDimensions,
  VirtualizedList,
} from 'react-native';
import {useFolder} from '../../hooks/useFolder';
import {EditorLayer, EncoderProject, FolderType} from '../../types';
import {useRoute} from '@react-navigation/native';
import {
  AnimatedFAB,
  Divider,
  IconButton,
  List,
} from 'react-native-paper';

import DragList from 'react-native-draglist';
import {ScrollView} from 'react-native-gesture-handler';
import {EditorRuler} from './editorruler';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {
  setScrollPosition,
  setScrollParams,
  setCaretPosition,
  setIsPlaying,
  setPixelsPerSecond,
} from '../../store/slices/editor';
import uuid from 'react-native-uuid';
import {useMutation, useQuery} from 'react-query';
import {
  getById,
  getFolderContent,
  getUserData,
  writeItem,
} from '../../api';
import EditorCaret from './editorcaret';
import {ProjectParams} from '../../types/nav';
import {
  EditorLayerBodyComponent,
  EditorLayerHeaderComponent,
} from './editorlayer';
import Editorbanner from './banner/editorbanner';
const folderType = FolderType.editor;

function EditorProjectComponent() {
  const {width} = useWindowDimensions();
  const isMobile = width < 700;
  const route = useRoute();
  const userId = useQuery('userData', async () => await getUserData())
    .data?.id as string;
  const scrollPosition = useAppSelector(
    state => state.editor.scrollPosition,
  );
  const scrollParams = useAppSelector(
    state => state.editor.scrollParams,
  );
  const {projectId, parentFolderId} = route.params as ProjectParams;
  // console.info('parentFolderId', parentFolderId)
  const getByIdCallback = useCallback(
    (itemId: string) => getById(itemId, parentFolderId),
    [parentFolderId],
  );
  const dispatch = useAppDispatch();
  const dataHook = useFolder(
    projectId,
    getFolderContent,
    getByIdCallback,
    FolderType.editor,
    writeItem,
  );
  useEffect(() => {
    console.info('reset');
    dispatch(setIsPlaying(false));
    dispatch(setScrollPosition(0));
    dispatch(setCaretPosition(0));
    return () => {
      dispatch(setIsPlaying(false));
      dispatch(setScrollPosition(0));
      dispatch(setCaretPosition(0));
      // dispatch(setV)
    };
  }, [projectId]);
  const writeItemMutation = useMutation((item: EditorLayer) =>
    writeItem(item),
  );
  const onClickAddLayer = async () => {
    const layer: EditorLayer = {
      id: uuid.v4().toString(),
      title: 'Layer' + ((dataHook.folderContent?.length || 0) + 1),
      folderId: projectId,
      userId,
      isEditorLayer: true,
      orderId: (dataHook.folderContent?.length || 0) + 1,
      folderType,
      isFolder: false,
      date: new Date().valueOf(),
      mnemonic: dataHook.folder?.mnemonic,
      editor: {
        isCommentLayer: false,
      },
    };
    await writeItemMutation.mutateAsync(layer);
    dataHook.folderContentQuery.refetch();
  };
  const renderContent = ({
    item,
    onDragEnd,
    onDragStart,
    index,
  }: {
    item: EncoderProject;
  }) => (
    <EditorLayerHeaderComponent
      key={item.id}
      onDragEnd={onDragEnd}
      onDragStart={onDragStart}
      folderId={item.id}
      layerIndex={index}
      parentHook={dataHook}
      parentFolderId={parentFolderId}
      projectId={projectId}
    />
  );
  const horizontalScroll = useRef();
  const [isOpen, setIsOpen] = useState(true);
  // const
  const contentContainerWidth = isMobile
    ? isOpen
      ? '60%'
      : '90%'
    : '80%';
  const controlsContainerWidth = isMobile
    ? isOpen
      ? '40%'
      : 0
    : '20%';

  const pixelsPerSecond = useAppSelector(
    state => state.editor.pixelsPerSecond,
  );
  const onClickSetScale = (scale: '+' | '-') => {
    let nscale =
      scale === '+' ? pixelsPerSecond * 1.25 : pixelsPerSecond * 0.75;
    if (nscale < 0.5) nscale = 0.5;
    if (nscale > 90) nscale = 90;
    dispatch(setPixelsPerSecond(nscale));
  };
  return (
    <View
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        height: '100%',
      }}>
      <View
        style={{height: isMobile ? 'auto' : '61%', width: '100%'}}>
        <Editorbanner
          projectId={projectId}
          routeParams={route.params}
        />
      </View>
      <ScrollView
        style={{
          width: '100%',
          height: isMobile ? 'auto' : '39%',
          display: 'flex',
        }}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}>
        <View style={{display: 'flex', flexDirection: 'column'}}>
          <View style={{width: '100%', flexDirection: 'row'}}>
            <View style={{width: controlsContainerWidth}}>
              {dataHook.folderContent !== undefined && (
                <DragList
                  renderItem={renderContent}
                  keyExtractor={item =>
                    item.id || item.date.toString()
                  }
                  data={dataHook.folderContent}
                  onReordered={async (start, end) => {
                    await dataHook.onReordered(start, end);
                  }}
                  showsHorizontalScrollIndicator={false}
                  showsVerticalScrollIndicator={false}
                />
              )}
            </View>
            <ScrollView
              horizontal={true}
              style={{
                width: contentContainerWidth,
                display: 'flex',
                flexDirection: 'row',
                height: '100%',
              }}
              onScroll={e => {
                const pos = e.nativeEvent.contentOffset.x;
                dispatch(setScrollPosition(pos));
              }}
              ref={e => {
                horizontalScroll.current = e;
                const totalWidth =
                  horizontalScroll?.current?.clientWidth;
                const totalScroll =
                  horizontalScroll.current?.scrollWidth;
                if (
                  scrollParams.totalScroll !== totalScroll ||
                  scrollParams.totalWidth !== totalWidth
                ) {
                  dispatch(
                    setScrollParams({
                      totalWidth,
                      totalScroll,
                    }),
                  );
                }
                horizontalScroll?.current?.scrollTo({
                  x: scrollPosition,
                });
              }}
              scrollEventThrottle={isMobile ? 250 : 50}
              showsHorizontalScrollIndicator={false}
              showsVerticalScrollIndicator={false}>
              <>
                <EditorRuler />
                <VirtualizedList
                  renderItem={({item, index}) => (
                    <EditorLayerBodyComponent
                      key={item.id}
                      folderId={item.id}
                      layerIndex={index}
                      parentFolderId={parentFolderId}
                    />
                  )}
                  keyExtractor={item =>
                    item.id || item.date.toString()
                  }
                  getItem={dataHook.getContentItem}
                  getItemCount={dataHook.getContentCount}
                  data={dataHook.folderContent}
                  showsHorizontalScrollIndicator={false}
                  showsVerticalScrollIndicator={false}
                />
              </>
            </ScrollView>
          </View>
          <View
            style={{
              width: controlsContainerWidth,
              overflow: 'visible',
            }}>
            <Divider />
            <List.Item
              titleStyle={{fontSize: 14}}
              title={''}
              onPress={() => onClickAddLayer()}
              right={props => (
                <>
                  <IconButton
                    {...props}
                    icon={'plus'}
                    mode={'outlined'}
                  />
                </>
              )}
            />
          </View>
          <View
            style={{
              width: contentContainerWidth,
              position: 'absolute',
              height: 0,
              left: controlsContainerWidth,
            }}>
            <EditorCaret />
          </View>
        </View>
      </ScrollView>
      {isMobile && (
        <AnimatedFAB
          style={{
            position: 'absolute',
            bottom: 20,
            left: 20,
            width: 60,
          }}
          icon={
            isOpen ? 'arrow-collapse-left' : 'arrow-collapse-right'
          }
          onPress={() => setIsOpen(!isOpen)}
          label=" "
          extended={true}
        />
      )}
      <AnimatedFAB
        style={{
          position: 'absolute',
          bottom: 20,
          right: 90,
          width: 60,
        }}
        icon={'magnify-plus-outline'}
        onPress={() => onClickSetScale('+')}
        label=" "
        extended={true}
      />
      <AnimatedFAB
        style={{
          position: 'absolute',
          bottom: 20,
          right: 20,
          width: 60,
        }}
        icon={'magnify-minus-outline'}
        onPress={() => onClickSetScale('-')}
        label=" "
        extended={true}
      />
    </View>
  );
}

export default memo(EditorProjectComponent);
